import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | expired | timeout | error
  const timer = useRef(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    const tick = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          try { if (refresh) await refresh(); } catch { /* ignore */ }
          return;
        }
        if (data.payment_status === "expired" || data.status === "expired") {
          setState("expired");
          return;
        }
      } catch (e) {
        // keep polling; ignore transient errors
      }
      if (attempts.current >= 15) {
        setState("timeout");
        return;
      }
      timer.current = window.setTimeout(tick, 2000);
    };
    tick();
    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const goDashboard = () => navigate("/dashboard");

  return (
    <div className="max-w-md mx-auto py-16 px-4" data-testid="payment-success-page">
      <Card className="p-8 border-zinc-200 text-center">
        {state === "polling" && (
          <>
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto" />
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-slate-600">Hang tight — we&apos;re syncing with Stripe. This usually takes a few seconds.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" data-testid="payment-success-icon" />
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">You&apos;re in</h1>
            <p className="mt-2 text-sm text-slate-600">Your subscription is active. Welcome to PostQik.</p>
            <Button onClick={goDashboard} className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="go-dashboard">
              Go to dashboard
            </Button>
          </>
        )}
        {state === "expired" && (
          <>
            <XCircle className="w-12 h-12 text-amber-500 mx-auto" />
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Session expired</h1>
            <p className="mt-2 text-sm text-slate-600">Your checkout session timed out. Try again from the Pricing page.</p>
            <Link to="/pricing"><Button className="mt-6 w-full bg-zinc-900 hover:bg-zinc-800 text-white">Back to Pricing</Button></Link>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="w-12 h-12 text-amber-500 mx-auto" />
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Still processing…</h1>
            <p className="mt-2 text-sm text-slate-600">
              Payments sometimes take a moment. Your dashboard will update automatically once Stripe confirms.
            </p>
            <Button onClick={goDashboard} className="mt-6 w-full bg-zinc-900 hover:bg-zinc-800 text-white">Go to dashboard</Button>
          </>
        )}
      </Card>
    </div>
  );
}
